import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Newspaper, RefreshCw, MapPin, ExternalLink } from "lucide-react";

export function AfdArticlesCard() {
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/afd/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/afd/recent").then((r) => setItems(r.data.items || [])).catch(() => {});
  };
  useEffect(load, []);

  const toggle = async (enabled) => {
    try { const { data } = await api.put("/afd/config", { enabled }); setCfg(data); toast.success(enabled ? "Auto-drafting on" : "Auto-drafting off"); }
    catch { toast.error("Save failed"); }
  };
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/afd/generate"); toast.success(data.count ? `Published ${data.count} new article(s)` : "No new discussions right now"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); } finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <div className="glass rounded-sm border border-amber-500/40 p-6" data-testid="afd-articles-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Newspaper className="h-5 w-5 text-amber-300" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Auto Forecast Articles</div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-200">
          <input type="checkbox" data-testid="afd-enabled" checked={!!cfg.enabled} onChange={(e) => toggle(e.target.checked)} className="h-4 w-4 accent-amber-500" />
          {cfg.enabled ? "On" : "Off"}
        </label>
      </div>
      <p className="mt-1 text-sm text-gray-300">Turns each office's latest NWS Area Forecast Discussion into a plain-language article (Claude), auto-publishes it, and teases the headline on the stream ticker. A new discussion drafts automatically.</p>

      <div className="mt-3 flex flex-wrap gap-2">
        {(cfg.offices || []).map((o) => (
          <span key={o.office} className="flex items-center gap-1 rounded-full border border-white/15 bg-black/30 px-3 py-1 text-xs text-gray-300"><MapPin className="h-3 w-3 text-amber-400" /> {o.region} <span className="text-gray-500">({o.office})</span></span>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button data-testid="afd-generate" onClick={generate} disabled={busy}
          className="flex items-center gap-2 rounded-sm bg-amber-500 px-4 py-2 font-oswald text-sm font-bold uppercase tracking-widest text-[#0a0f1c] hover:bg-amber-400 disabled:opacity-40">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Writing…" : "Generate now"}
        </button>
        <span className="text-xs text-gray-400">Drafts a fresh article for any office with a new discussion.</span>
      </div>

      <div className="mt-5">
        <div className="mb-2 font-oswald text-[11px] uppercase tracking-widest text-gray-400">Recent auto-articles</div>
        <div className="flex flex-col gap-2" data-testid="afd-recent">
          {items.length === 0 && <div className="rounded-sm border border-dashed border-white/15 px-4 py-6 text-center text-sm text-gray-500">No forecast articles yet.</div>}
          {items.map((it) => (
            <a key={it.id} href={`/forecast/${it.id}`} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-3 rounded-sm border border-white/10 bg-black/30 px-4 py-2.5 hover:border-amber-500/50" data-testid={`afd-item-${it.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white">{it.title}</div>
                <div className="text-xs text-gray-400">{it.region} · {it.published_at ? new Date(it.published_at).toLocaleString() : ""}</div>
              </div>
              <ExternalLink className="h-4 w-4 shrink-0 text-gray-500" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
